/**
 * Generic, theme-aware, client-side data table.
 *
 * The standard list surface for the console: search + filter dropdowns +
 * pagination (default 10) + per-row actions, all in one place so every page
 * (Rules, AgentRunners, ScheduleView, Capabilities, SbomManager) reads as one
 * system. Uses the CSS-variable idiom (`--bg`, `--ink`, `--border`,
 * `--accent`) so it renders correctly in light + `.dark`.
 *
 * All filtering/paging is client-side — the lists are small and already fully
 * loaded. `ponytail:` add server-side paging when a list grows past a few
 * hundred rows (push `page`/`search`/`filter` into query params + a `total`).
 */
import { useEffect, useMemo, useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import { Search, ChevronLeft, ChevronRight, Inbox } from 'lucide-react';
import { SkeletonTable } from './Skeleton';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig<T> {
  key: string;
  label: string;
  options: FilterOption[];
  /** Row matches the filter when this returns true for the selected value. */
  predicate: (row: T, value: string) => boolean;
}

export interface RowAction<T> {
  key: string;
  /** Static label, or a per-row resolver (e.g. Approve↔Ban, Ignore↔Reactivate). */
  label: string | ((row: T) => string);
  icon?: ComponentType<{ className?: string }>;
  onClick: (row: T) => void;
  disabled?: (row: T) => boolean;
  tone?: 'default' | 'danger';
  title?: string;
}

export interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Accessors used for the client-side search box. */
  searchKeys?: ((row: T) => string)[];
  searchPlaceholder?: string;
  filters?: FilterConfig<T>[];
  /** Per-row action buttons (renders a trailing Actions column). */
  actions?: RowAction<T>[];
  /** Page size; default 10. Set to 0 to disable pagination. */
  pageSize?: number;
  /** Column widths (percentage strings) forwarded to SkeletonTable while loading. */
  skeletonCols?: string[];
  emptyState?: ReactNode;
  /** Right-aligned slot above the table (Add button, bulk actions, etc.). */
  toolbar?: ReactNode;
  /** Dense rows (smaller padding) for wide tables like SBOM. */
  dense?: boolean;
  /** Click anywhere on a row to fire this (e.g. open detail). Rows get a
   *  pointer cursor + hover tint when set. */
  onRowClick?: (row: T) => void;
}

const inputCls = 'rounded-md text-xs focus:outline-none';
const inputStyle: CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  color: 'var(--ink)',
};

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  loading = false,
  searchKeys,
  searchPlaceholder = 'Search…',
  filters = [],
  actions,
  pageSize = 10,
  skeletonCols,
  emptyState,
  toolbar,
  dense = false,
  onRowClick,
}: DataTableProps<T>) {
  const [query, setQuery] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);

  const activeFilters = filters.filter((f) => filterValues[f.key] && filterValues[f.key] !== 'all');

  // Prune filter selections whose value is no longer offered — e.g. a refetch
  // drops the selected repo while ≥2 others remain. Without this the controlled
  // <select> falls back to "All" but the predicate still excludes every row,
  // leaving the user stuck on an empty table they can't clear. Returns the same
  // `prev` reference when nothing is stale so React bails out (no re-render).
  useEffect(() => {
    setFilterValues((prev) => {
      let next = prev;
      for (const f of filters) {
        const v = prev[f.key];
        if (v && v !== 'all' && !f.options.some((o) => o.value === v)) {
          if (next === prev) next = { ...prev };
          delete next[f.key];
        }
      }
      return next;
    });
  }, [filters]);

  // Apply search + filters.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (q && searchKeys) {
        const hit = searchKeys.some((acc) => acc(row).toLowerCase().includes(q));
        if (!hit) return false;
      }
      for (const f of activeFilters) {
        if (!f.predicate(row, filterValues[f.key])) return false;
      }
      return true;
    });
  }, [rows, query, searchKeys, activeFilters, filterValues]);

  // Pagination math.
  const paging = pageSize > 0;
  const total = filtered.length;
  const maxPage = paging ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  // Clamp the page state when the result set shrinks below the current page
  // (e.g. an async refetch lands while a filter is active) so the user never
  // stares at an empty page or lands mid-list after clearing a filter.
  useEffect(() => { if (page > maxPage) setPage(maxPage); }, [page, maxPage]);
  const safePage = Math.min(page, maxPage);
  const start = paging ? (safePage - 1) * pageSize : 0;
  const end = paging ? Math.min(start + pageSize, total) : total;
  const pageRows = paging ? filtered.slice(start, start + pageSize) : filtered;

  const colWidths = skeletonCols ?? columns.map((c) => c.width ?? `${Math.floor(100 / columns.length)}%`);

  const setFilter = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetPageOnQuery = (v: string) => { setQuery(v); setPage(1); };

  const showControls = Boolean(searchKeys || filters.length || toolbar);
  const cellPad = dense ? 'px-3 py-1.5' : 'px-4 py-2.5';

  return (
    <div className="space-y-3">
      {showControls && (
        <div className="flex items-center gap-2 flex-wrap">
          {searchKeys && (
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--ink-secondary)' }} />
              <input
                value={query}
                onChange={(e) => resetPageOnQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className={`${inputCls} pl-8 pr-3 py-1.5 w-56`}
                style={inputStyle}
              />
            </div>
          )}
          {filters.map((f) => (
            <select
              key={f.key}
              value={filterValues[f.key] ?? 'all'}
              onChange={(e) => setFilter(f.key, e.target.value)}
              className={`${inputCls} px-2 py-1.5`}
              style={inputStyle}
              aria-label={f.label}
            >
              <option value="all">{f.label}: All</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ))}
          {toolbar && <div className="ml-auto flex items-center gap-2">{toolbar}</div>}
        </div>
      )}

      {loading ? (
        <SkeletonTable rows={Math.min(pageSize || 6, 6)} cols={colWidths} />
      ) : total === 0 ? (
        <div className="py-10 text-center text-xs rounded-lg border" style={{ color: 'var(--ink-secondary)', borderColor: 'var(--border)' }}>
          {emptyState ?? (
            <>
              <Inbox className="w-5 h-5 mx-auto mb-2 opacity-60" />
              No results{query || activeFilters.length ? ' match your filters' : ''}.
            </>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider border-b" style={{ color: 'var(--ink-secondary)', borderColor: 'var(--border)' }}>
                  {columns.map((c) => (
                    <th key={c.key} className={`${cellPad} font-semibold align-top`} style={{ width: c.width, textAlign: c.align ?? 'left' }}>
                      {c.header}
                    </th>
                  ))}
                  {actions && actions.length > 0 && (
                    <th className={`${cellPad} font-semibold`} style={{ width: `${actions.length * 36}px`, textAlign: 'right' }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={`align-top border-t transition${onRowClick ? ' cursor-pointer' : ''}`}
                    style={{ borderColor: 'var(--border)' }}
                    onMouseEnter={onRowClick ? (e) => { e.currentTarget.style.background = 'var(--accent-bg)'; } : undefined}
                    onMouseLeave={onRowClick ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}
                  >
                    {columns.map((c) => (
                      <td key={c.key} className={cellPad} style={{ textAlign: c.align ?? 'left' }}>
                        {c.render(row)}
                      </td>
                    ))}
                    {actions && actions.length > 0 && (
                      <td className={cellPad}>
                        <div className="flex items-center justify-end gap-0.5">
                          {actions.map((a) => {
                            const disabled = a.disabled?.(row) ?? false;
                            const Icon = a.icon;
                            const labelText = typeof a.label === 'function' ? a.label(row) : a.label;
                            return (
                              <button
                                key={a.key}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); a.onClick(row); }}
                                disabled={disabled}
                                className="p-1.5 rounded-md transition disabled:opacity-30 disabled:cursor-default"
                                style={{ color: a.tone === 'danger' ? '#dc2626' : 'var(--ink-secondary)' }}
                                title={a.title ?? labelText}
                                aria-label={labelText}
                              >
                                {Icon ? <Icon className="w-3.5 h-3.5" /> : <span className="text-xs">{labelText}</span>}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {paging && (
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-t text-[11px]" style={{ borderColor: 'var(--border)', color: 'var(--ink-secondary)' }}>
              <span>
                {total > 0 ? `Showing ${start + 1}–${end} of ${total}` : 'No results'}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="p-1 rounded-md transition disabled:opacity-30 disabled:cursor-default"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-1.5 font-medium" style={{ color: 'var(--ink)' }}>{safePage} / {maxPage}</span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                  disabled={safePage >= maxPage}
                  className="p-1 rounded-md transition disabled:opacity-30 disabled:cursor-default"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
